import type { Zone } from "@two-crowns/data";
import { distToPolyline } from "./geometry";

/**
 * Deterministic procedural heightmap shared by client (mesh + entity height) and server
 * (future: line of sight, fall checks). Pure function of (x, z, params).
 */
export interface TerrainParams {
  seed: number;
  amplitude: number;
  scale: number;
  flatRadius: number;
}

function hash(ix: number, iz: number, seed: number): number {
  let h = (ix * 374761393 + iz * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smooth(x - ix);
  const fz = smooth(z - iz);
  const a = hash(ix, iz, seed);
  const b = hash(ix + 1, iz, seed);
  const c = hash(ix, iz + 1, seed);
  const d = hash(ix + 1, iz + 1, seed);
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}

export function terrainHeight(
  x: number,
  z: number,
  t: TerrainParams,
  center: { x: number; z: number } = { x: 0, z: 0 },
): number {
  let h = 0;
  let amp = 1;
  let freq = 1 / t.scale;
  let norm = 0;
  for (let o = 0; o < 3; o++) {
    h += valueNoise(x * freq, z * freq, t.seed + o * 101) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  const d = Math.hypot(x - center.x, z - center.z);
  const flat =
    t.flatRadius > 0 ? smooth(Math.min(1, Math.max(0, (d - t.flatRadius) / t.flatRadius))) : 1;
  return (h / norm) * t.amplitude * flat;
}

/** The ground height anywhere in a zone: hills, border mountains and carved water beds. */
export function zoneHeight(zone: Zone, x: number, z: number): number {
  const t = zone.terrain;
  const half = zone.size / 2;
  let h = terrainHeight(x, z, t, zone.spawn);
  // Gentle near town, rugged far out.
  if (t.farBoost !== undefined && t.farBoost !== 1) {
    const far = Math.min(1, Math.hypot(x - zone.spawn.x, z - zone.spawn.z) / half);
    h *= 1 + (t.farBoost - 1) * smooth(far);
  }
  // Mountains along the border (a natural wall instead of an invisible one).
  if (t.edgeStart !== undefined && t.edgeHeight) {
    const m = Math.max(Math.abs(x), Math.abs(z));
    if (m > t.edgeStart) {
      const e = smooth(Math.min(1, (m - t.edgeStart) / Math.max(1, half - t.edgeStart)));
      const ridge = 0.6 + 0.4 * valueNoise(x / 23, z / 23, t.seed + 7);
      h += e * t.edgeHeight * ridge;
    }
  }
  // Nation bases (the PvP colony) sit on level ground.
  const bases = zone.bases;
  if (bases) {
    for (const b of [bases.human, bases.orc]) {
      const d = Math.hypot(x - b.x, z - b.z);
      const r = bases.safeRadius + 8;
      if (d < r * 2) h *= smooth(Math.max(0, (d - r) / r));
    }
  }
  // An arena: a level floor ringed by banks that rise to the rim, then ease back down to the
  // hills outside. Built upward so the floor stays above the zone's water plane.
  const bowl = zone.bowl;
  if (bowl) {
    const d = Math.hypot(x - bowl.x, z - bowl.z);
    const fall = bowl.radius - bowl.floorRadius;
    if (d < bowl.floorRadius) h = 0;
    else if (d < bowl.radius) h = bowl.depth * smooth((d - bowl.floorRadius) / fall);
    else if (d < bowl.radius + fall)
      h = h + (bowl.depth - h) * (1 - smooth((d - bowl.radius) / fall));
  }
  const w = zone.world?.water;
  if (w) {
    const d = waterDistance(zone, x, z);
    if (d < WATER_BANK_M + WATER_VALLEY_M) {
      // Bed, a short bank up to just above the water, then a wide valley back to the hills.
      const bed = w.level - w.depth;
      const shore = w.level + 0.25;
      if (d <= 0) h = bed;
      else if (d < WATER_BANK_M) h = bed + (shore - bed) * smooth(d / WATER_BANK_M);
      else h = shore + (h - shore) * smooth((d - WATER_BANK_M) / WATER_VALLEY_M);
    }
  }
  return h;
}

/** Width of the sloping bank around rivers and lakes. */
export const WATER_BANK_M = 5;
/** The land around water eases down into it over this distance. */
const WATER_VALLEY_M = 32;

/**
 * Signed distance to the nearest water: negative inside a river or lake, positive outside.
 * Used for carving the ground and keeping plants out of the water.
 */
export function waterDistance(zone: Zone, x: number, z: number): number {
  const w = zone.world?.water;
  if (!w) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (const r of w.rivers) best = Math.min(best, distToPolyline(x, z, r.points) - r.width / 2);
  for (const l of w.lakes) best = Math.min(best, Math.hypot(x - l.x, z - l.z) - l.r);
  return best;
}

/** Bridge decks sit this far above the water surface. */
export const BRIDGE_DECK_ABOVE_WATER = 0.7;
export const BRIDGE_HALF_W = 2.5;
export const BRIDGE_HALF_L = 11;

/** Where characters stand: the ground, or a bridge deck over it. */
export function groundHeight(zone: Zone, x: number, z: number): number {
  const h = zoneHeight(zone, x, z);
  const level = zone.world?.water.level;
  if (level === undefined) return h;
  for (const s of zone.town?.structures ?? []) {
    if (s.type !== "bridge") continue;
    const r = (s.rot * Math.PI) / 180;
    const dx = x - s.x;
    const dz = z - s.z;
    const lx = dx * Math.cos(r) - dz * Math.sin(r);
    const lz = dx * Math.sin(r) + dz * Math.cos(r);
    if (Math.abs(lx) <= BRIDGE_HALF_W && Math.abs(lz) <= BRIDGE_HALF_L)
      return Math.max(h, level + BRIDGE_DECK_ABOVE_WATER);
  }
  return h;
}

/**
 * How far into a region a point is: < 1 inside, > 1 outside. The edge wobbles with noise so
 * forests and meadows get ragged, natural borders instead of circles.
 */
export function regionReach(
  region: { x: number; z: number; r: number },
  x: number,
  z: number,
  seed = 0,
): number {
  const wobble = 0.78 + 0.44 * valueNoise(x / 38 + region.x, z / 38 + region.z, seed + 991);
  return Math.hypot(x - region.x, z - region.z) / (region.r * wobble);
}
